/**
 * ENG-2595 — Save → labelled.json exporter (P&ID Annotation Tool).
 *
 * Turns the saved annotation graph into the ground-truth JSON the P&ID-detection
 * eval consumes. The target extends the eval's `GoldDrawing`
 * (backend/core/src/intai/evals/ingestion_accuracy/gold_schema.py):
 *
 *   { drawing_key, sheet_id?, page, image_size:{width,height},
 *     symbols: [{ id, bbox:[x1,y1,x2,y2], category, subcategory, type, tag, annotation_text }],
 *     lines:   [{ id, polyline:[[x,y],…],  category, subcategory, type, tag, annotation_text }],
 *     tags:    [{ id, bbox:[x1,y1,x2,y2], text, symbol_id, annotation_text }],
 *     connections: [{ from, to, via }],  // via = the connecting line id
 *     tables:  [{ table_index, bbox, n_rows, n_cols,
 *                 cells: [{ id, row, col, text, bbox, type }] }] }
 *
 * Schema (Amin, 2026-06-23):
 *   1. bbox + polyline are NORMALIZED to [0,1] by the drawing's render dims (default).
 *      Avoids pixel-space bugs; the eval normalizes its predictions to match. `image_size`
 *      is still emitted (informational / lets a consumer denormalize).
 *   2. symbols AND lines carry the full human classification — category/subcategory/type
 *      (= pnid_level_i/ii/iii) + tag (= the annotator's Tag/label) + annotation_text (free
 *      text, always present; '' when unset). `type` is the leaf class. Objects without a leaf
 *      Type are DROPPED (drops detector noise: false_positive_valve, piping_arrow_*,
 *      unclassified line segments, …) EXCEPT Annotation-category objects, which are Type-exempt
 *      (their PIC001 subcategories have no leaf Type) and export with `type: ''`.
 *   3. tags are first-class: each carries its own bbox + OCR text + a `symbol_id` link +
 *      `annotation_text` (always present, '' when unset — mirrors symbols/lines), so OCR and
 *      tag→symbol association can be scored separately. The link is the saved `has_label` edge
 *      (symbol → tag). `symbol_id` is null when the tag has no link, or its symbol dropped by rule (2).
 *   4. connections are LINE-REFERENCED and ANY-TO-ANY (ENG-2583/2613): each carries the connecting
 *      line in `via`, and `from`/`to` may be ANY kept object — a symbol, a Signal Tee (kept as a
 *      symbol), or ANOTHER line. Connectivity edges are oriented `via-line → target`; we collect
 *      the objects each kept line's endpoints attach to and emit one `{ from, to, via: line }` per
 *      pair. So symbol↔symbol, symbol↔Tee, object↔line and line↔line are all expressible (a line
 *      endpoint can snap onto a polygon or another line, #20250; a placed Tee wires to lines, #20317).
 *      NOT proximity-inferred. A line with one attached endpoint yields nothing; two distinct lines
 *      between the same objects stay distinct (deduped per from+to+via).
 *   5. tables carry the material balance tables in their ROW/COLUMN layout, not as loose
 *      boxes: one entry per `table_index`, `cells[]` row-major, each with its `(row, col)`,
 *      text and bbox. Row 0 is the column-header band, col 0 the row-header band — the same
 *      convention the KG's table reconstruction uses. `type` is the PIC001 leaf, '' for a
 *      body cell. An EMPTY cell is still exported: a blank in a material balance table means
 *      the stream has no value for that property, unlike a tag with no OCR text. Table cells
 *      are NOT also emitted into `symbols[]`/`tags[]` — double-counting them would inflate
 *      every symbol metric. For the same reason a `tags[]` entry whose box falls inside an
 *      exported table is DROPPED: the table already carries that text, cell by cell. This is
 *      what lets the editor build a table from OCR text without deleting the text. A merged
 *      header carries `col_span` and the columns it covers hold no cell — so a stream number
 *      sitting over its BASE/ALTERNATE pair is ONE observation, as the drawing shows it,
 *      rather than the same text duplicated into both.
 *
 * Filtering:
 *   - symbols: type==='symbol', usable bbox required, leaf Type required (except Annotation); classified
 *     junctions (Signal Tees) are kept, raw unclassified junctions drop via the no-leaf-Type rule;
 *   - lines:   type==='connection', usable polyline (≥2 pts) required; kept if classified (leaf Type),
 *     Annotation, OR an annotator-drawn connector (has endpoint-link edges) — unclassified parsed
 *     noise lines (no edges) drop. Connector lines don't need a Type (their role is connectivity);
 *   - tags:    type==='tag', usable bbox required, non-empty text required.
 *   - connections: per kept line, its start/end symbol edges → {from, to, via:line}; both ends kept; deduped per from+to+via.
 *   - tables:  type==='table_cell' with usable row/col/table_index + bbox; a table whose
 *     every cell fails those checks is omitted.
 *
 * Data source = the SAVED graph (Postgres-backed store), not the stored ML
 * aggregated_data.json. Image dimensions come from the rendered page (the GraphEditor's
 * loaded background image — `imageSize`), the space the node geometry lives in.
 */

import { groupCellsIntoTables, cellKey, cellText } from './tableGrid'

/** (x1, y1, x2, y2) corner bbox — matches gold_schema.BBox. Normalized [0,1] by default. */
export type GoldBBox = [number, number, number, number]

export interface GoldSymbol {
  id: string
  bbox: GoldBBox
  /** PIC001 Level I (pnid_level_i). */
  category: string
  /** PIC001 Level II (pnid_level_ii). */
  subcategory: string
  /** PIC001 leaf Type (pnid_level_iii) — the GT class. Required for non-Annotation objects
   *  (dropped if empty); Annotation-category objects are Type-exempt and export with `type: ''`. */
  type: string
  /** Annotator-entered identifier (the symbol's `label` / Tag field); '' if none. */
  tag: string
  /** Annotator-entered free-text annotation; always present, '' if none. */
  annotation_text: string
}

export interface GoldLine {
  id: string
  /** Ordered, normalized [0,1] polyline points parsed from the connection node's geometry_wkt. */
  polyline: Array<[number, number]>
  /** PIC001 Level I (pnid_level_i). */
  category: string
  /** PIC001 Level II (pnid_level_ii). */
  subcategory: string
  /** PIC001 leaf Type (pnid_level_iii). Required for non-Annotation lines (dropped if empty);
   *  Annotation-category lines are Type-exempt and export with `type: ''`. */
  type: string
  /** Annotator-entered identifier (the line's `label` / Tag field); '' if none. */
  tag: string
  /** Annotator-entered free-text annotation; always present, '' if none. */
  annotation_text: string
}

export interface GoldTag {
  id: string
  bbox: GoldBBox
  /** OCR'd identifier text (e.g. "ZV+1075") — for scoring OCR independently. */
  text: string
  /** Linked symbol id (has_label edge), or null if unlinked / its symbol was dropped. */
  symbol_id: string | null
  /** Annotator-entered free-text annotation; always present, '' if none (mirrors GoldSymbol). */
  annotation_text: string
}

export interface GoldConnection {
  /** One endpoint object id the connecting line attaches to — a `symbols[]` id (incl. a Signal
   *  Tee) OR a `lines[]` id (object↔line / line↔line). */
  from: string
  /** The other endpoint object id (same domain as `from`: a `symbols[]` or `lines[]` id). */
  to: string
  /** The connecting line id (always present in `lines[]`). Keeps the drawn line IN the
   *  relationship instead of collapsing to a bare endpoint pair — so a line is never lost and
   *  two distinct lines between the same objects stay distinct. */
  via: string
}

/** One cell of a material balance table. */
export interface GoldTableCell {
  id: string
  /** 0-based row; row 0 is the column-header band (stream numbers / names). */
  row: number
  /** 0-based column; col 0 is the row-header band (property names). */
  col: number
  /** The cell's value as annotated. '' is a real, meaningful value here — kept, not dropped. */
  text: string
  bbox: GoldBBox
  /** PIC001 leaf (pnid_level_iii) — a header band's type, '' for a body cell. */
  type: string
  /** Columns this cell occupies. Omitted when 1. A merged header (a stream number sitting
   *  over its BASE/ALTERNATE pair) spans 2, and the columns it covers carry no cell. */
  col_span?: number
}

export interface GoldTable {
  /** Which table on the drawing, as stamped by the parser (or assigned on manual entry). */
  table_index: number
  /** Footprint of the table: the union of its cells' boxes. */
  bbox: GoldBBox
  n_rows: number
  n_cols: number
  /** Row-major (row, then col). Sparse: a coordinate the drawing never filled is absent. */
  cells: GoldTableCell[]
}

export interface GoldImageSize {
  width: number
  height: number
}

export interface GoldDrawing {
  drawing_key: string
  sheet_id?: string
  page: number
  image_size: GoldImageSize
  symbols: GoldSymbol[]
  lines: GoldLine[]
  tags: GoldTag[]
  /** Line-referenced, any-to-any connectivity: {from,to} are kept object ids (symbol / Signal Tee /
   *  line) + `via` = the connecting line (ENG-2583/2613). */
  connections: GoldConnection[]
  /** Material balance tables in their row/column layout. Empty when the drawing has none —
   *  always present, so a consumer can read it unconditionally. */
  tables: GoldTable[]
}

/**
 * Minimal structural shape of the graph nodes the exporter needs. The store's
 * `SymbolNode`/`TagNode`/`ConnectionNode` (frontend/web/src/service/graph.ts) satisfy this via
 * structural typing; kept narrow so the function is decoupled and unit-testable with plain objects.
 */
export interface ExportNode {
  id: string
  type: string
  x?: number
  y?: number
  width?: number
  height?: number
  /** PIC001 classification — Category / Subcategory / leaf Type. */
  pnid_level_i?: string
  pnid_level_ii?: string
  pnid_level_iii?: string
  symbol_type?: string
  /**
   * Legacy model category (e.g. 'junction') — NOT the exported `GoldSymbol.category`, which comes
   * from `pnid_level_i`. No longer read by the exporter (a junction is now kept/dropped purely by
   * its leaf Type, like any other object); retained for structural parity with the store's `SymbolNode`.
   */
  category?: string
  /** Tag-node identifier text (TagNode.content). */
  content?: string
  /** Symbol/line denormalized label (the annotator Tag field); also a tag-text fallback. */
  label?: string
  /** Connection-node polyline geometry (WKT (MULTI)LINESTRING). */
  geometry_wkt?: string
  /** Annotator-entered free-text annotation; applies to any node type/category, '' when unset. */
  annotation_text?: string
  /** Table-cell coordinates — 0-based; row 0 / col 0 are the header bands. */
  row?: number
  col?: number
  table_index?: number
  /** Columns a merged cell occupies; absent/1 for a normal cell. */
  col_span?: number
  /** Table-cell display text; `content` is the primary field, `name` its mirror. */
  name?: string
}

export interface ExportEdge {
  fromNodeId: string
  toNodeId: string
  /** Edge relationship, e.g. 'has_label' (symbol→tag). */
  relation?: string
}

export interface ExportMeta {
  drawingKey: string
  sheetId?: string
  page?: number
}

export interface BuildGoldDrawingOptions {
  /** Emit normalized [0,1] coords (default true — the locked spec). Set false for absolute px. */
  normalize?: boolean
}

/** Edge relationship that links a symbol to its identifier tag (backend: add_edge(symbol, tag, 'has_label')). */
const HAS_LABEL = 'has_label'

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** Clamp to [0,1] so a normalized coord can never silently exceed the unit square (off-canvas / oversized node). */
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

/** A bbox is usable only if it stays ordered; a clamped fully-off-canvas box collapses to a line/point. */
const isOrdered = (b: GoldBBox): boolean => b[2] > b[0] && b[3] > b[1]

const trimOrEmpty = (v: string | undefined): string => (v || '').trim()

/**
 * PIC001 Level-I category whose objects are Type-exempt: Annotation subcategories
 * (Title Block / Attribute / Slope / …) have no leaf Type, so they're kept in the
 * export and never counted as "dropped for missing Type". Named to prevent the
 * string drifting across the drop rules and the warning counter below.
 */
const ANNOTATION_CATEGORY = 'Annotation'

/**
 * Parse a (MULTI)LINESTRING WKT into ordered [x, y] points (absolute px).
 *
 * Robust to WKT variance rather than matching one exact layout:
 *  - arbitrary spacing / paren style (`LINESTRING(1 2,3 4)`, `MULTILINESTRING((..),(..))`);
 *  - dimension modifiers `Z` / `M` / `ZM` (`LINESTRING Z (1 2 0, …)`) — PostGIS emits these
 *    for some drivers; we pull each "x y" pair so a 3rd (Z) ordinate is naturally skipped.
 * Multi-segment geometry is flattened into ONE ordered polyline, so a point shared between two
 * sub-segments appears once per segment. That duplicate is intentional (it preserves segment
 * order); do not "dedup" it. Returns [] for non-line WKT (e.g. POINT/POLYGON) or empty input.
 */
export const parsePolyline = (wkt: string | undefined): Array<[number, number]> => {
  if (!wkt || !/^\s*(?:MULTI)?LINESTRING/i.test(wkt)) return []
  const pts: Array<[number, number]> = []
  const pair = /(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g
  let m: RegExpExecArray | null
  while ((m = pair.exec(wkt)) !== null) pts.push([Number(m[1]), Number(m[2])])
  return pts
}

/**
 * Build the eval-consumable `GoldDrawing` payload from the saved annotation graph.
 * Pure: no app/store/DOM access — pass plain node/edge/imageSize objects.
 */
export function buildGoldDrawing(
  nodes: readonly ExportNode[],
  edges: readonly ExportEdge[],
  imageSize: GoldImageSize,
  meta: ExportMeta,
  opts: BuildGoldDrawingOptions = {},
): GoldDrawing {
  const { normalize = true } = opts

  if (!isFiniteNumber(imageSize?.width) || !isFiniteNumber(imageSize?.height) || imageSize.width <= 0 || imageSize.height <= 0) {
    throw new Error(`buildGoldDrawing: invalid image_size ${JSON.stringify(imageSize)}`)
  }
  const w = imageSize.width
  const h = imageSize.height

  // (x, y, width, height) → [x1, y1, x2, y2], normalized to [0,1] when requested.
  const toBBox = (x: number, y: number, width: number, height: number): GoldBBox => {
    const x2 = x + width
    const y2 = y + height
    // Normalized output is clamped to [0,1] — enforces the locked spec invariant at the
    // generation site even if a node was dragged off-canvas or resized past the image edge.
    return normalize ? [clamp01(x / w), clamp01(y / h), clamp01(x2 / w), clamp01(y2 / h)] : [x, y, x2, y2]
  }

  const toPoint = (x: number, y: number): [number, number] => (normalize ? [clamp01(x / w), clamp01(y / h)] : [x, y])

  // ── symbols (type==='symbol'; full classification; leaf Type required except Annotation) ───────
  const symbols: GoldSymbol[] = []
  const keptSymbolIds = new Set<string>()
  for (const node of nodes) {
    if (node.type !== 'symbol') continue // tag-text / connection have their own sections; table cells go to tables[], never here — double-counting would inflate every symbol metric
    const category = trimOrEmpty(node.pnid_level_i)
    const type = trimOrEmpty(node.pnid_level_iii)
    // ENG-2613: a placed Signal Tee is a `category:'junction'` symbol that IS classified
    // (Instrumentation ▸ Line ▸ Signal Tee), so it must serialize. We no longer blanket-drop
    // junctions; an unclassified raw detector-junction (category 'junction', no leaf Type) still
    // falls out via the no-leaf-Type rule below. A classified Tee lands in symbols[] with its
    // leaf class, which also makes it a valid connection endpoint.
    if (!type && category !== ANNOTATION_CATEGORY) continue // no PIC001 leaf Type → not useful ground truth (drops detector noise + raw junctions); Annotation objects are kept even without a leaf Type
    // Geometry must be a usable box.
    if (!isFiniteNumber(node.x) || !isFiniteNumber(node.y) || !isFiniteNumber(node.width) || !isFiniteNumber(node.height)) continue
    if (node.width <= 0 || node.height <= 0) continue
    const bbox = toBBox(node.x, node.y, node.width, node.height)
    if (!isOrdered(bbox)) continue // clamped off-canvas box collapsed to a line/point — not usable GT
    symbols.push({
      id: node.id,
      bbox,
      category,
      subcategory: trimOrEmpty(node.pnid_level_ii),
      type,
      tag: trimOrEmpty(node.label),
      annotation_text: trimOrEmpty(node.annotation_text),
    })
    keptSymbolIds.add(node.id)
  }

  // Node ids that participate in any edge. An annotator-drawn CONNECTOR line gets endpoint-link
  // edges when its ends are snapped to objects; a parsed/OCR line stores its topology elsewhere and
  // has NO edges. We use this to keep drawn connectors even when they lack a leaf Type (their job is
  // to connect, not to be classified) while still dropping unclassified parsed noise lines.
  const nodeIdsInEdges = new Set<string>()
  for (const e of edges) { nodeIdsInEdges.add(e.fromNodeId); nodeIdsInEdges.add(e.toNodeId) }

  // ── lines (type==='connection'; polyline geometry) ─────────────────────────────────────────────
  const lines: GoldLine[] = []
  const keptLineIds = new Set<string>()
  for (const node of nodes) {
    if (node.type !== 'connection') continue
    const category = trimOrEmpty(node.pnid_level_i)
    const type = trimOrEmpty(node.pnid_level_iii)
    // Keep a line if it's classified (leaf Type) OR Annotation OR it's an annotator-drawn connector
    // (has endpoint-link edges). An unclassified line with NO edges is parsed detector noise → drop.
    // Connector lines don't require a Type: their role is to carry connectivity (via), not to be a
    // classified object — so a drawn line linking two objects always survives and yields a connection.
    if (!type && category !== ANNOTATION_CATEGORY && !nodeIdsInEdges.has(node.id)) continue
    const raw = parsePolyline(node.geometry_wkt)
    if (raw.length < 2) continue // no usable polyline geometry
    lines.push({
      id: node.id,
      polyline: raw.map(([x, y]) => toPoint(x, y)),
      category,
      subcategory: trimOrEmpty(node.pnid_level_ii),
      type,
      tag: trimOrEmpty(node.label),
      annotation_text: trimOrEmpty(node.annotation_text),
    })
    keptLineIds.add(node.id)
  }

  // ── tags (first-class; symbol_id from the has_label edge: symbol → tag) ─────
  const symbolByTag = new Map<string, string>()
  for (const edge of edges) {
    if (edge.relation === HAS_LABEL) symbolByTag.set(edge.toNodeId, edge.fromNodeId)
  }
  const tags: GoldTag[] = []
  for (const node of nodes) {
    if (node.type !== 'tag') continue
    if (!isFiniteNumber(node.x) || !isFiniteNumber(node.y) || !isFiniteNumber(node.width) || !isFiniteNumber(node.height)) continue
    if (node.width <= 0 || node.height <= 0) continue
    const text = (node.content ?? node.label ?? '').trim()
    if (!text) continue // no OCR text → not useful ground truth
    const bbox = toBBox(node.x, node.y, node.width, node.height)
    if (!isOrdered(bbox)) continue // clamped off-canvas box — not usable GT
    const linked = symbolByTag.get(node.id)
    // Keep referential integrity: only point at a symbol that survived into symbols[].
    const symbol_id = linked && keptSymbolIds.has(linked) ? linked : null
    tags.push({ id: node.id, bbox, text, symbol_id, annotation_text: trimOrEmpty(node.annotation_text) })
  }

  // ── connections (line-referenced, any-to-any: {from, to, via:line}; ENG-2583/2613) ──────────
  // A drawn line records its connectivity as the annotator's confirmed endpoint clicks (NOT
  // proximity inference). Connectivity edges are oriented `via-line → target` (EditorWrapper:
  // the start anchor, end anchor, and junction wiring all write line → target). We collect the
  // objects each kept line's endpoints attach to, then emit one connection per pair of distinct
  // targets, carrying the line in `via`.
  //   - "any-to-any": a target is any KEPT object — a symbol, a Signal Tee (kept as a symbol),
  //     or ANOTHER line — so symbol↔symbol, symbol↔Tee, object↔line and line↔line all work
  //     (#20250 lets a line endpoint snap onto a polygon or another line; #20317 wires a placed
  //     Tee to nearby lines). `via` is always the connecting line.
  //   - the uniform via-line → target orientation is what makes this unambiguous: a target line
  //     never gets miscredited as the via (only the line that OWNS the edge is `fromNodeId`).
  //   - has_label (symbol → tag) and other non-line-origin edges are excluded (via must be a line);
  //     a line with only one attached endpoint yields nothing; self-links are skipped; dedup is
  //     per from+to+via so two distinct lines between the same objects stay distinct.
  const keptObjectIds = new Set<string>([...keptSymbolIds, ...keptLineIds]) // valid connection endpoints
  const targetsByLine = new Map<string, string[]>() // viaLineId → [endpoint object ids], in edge order
  const targetSetByLine = new Map<string, Set<string>>() // companion set for O(1) dedup (a T-junction can wire ≥3)
  const addTarget = (via: string, target: string) => {
    let set = targetSetByLine.get(via)
    if (!set) { set = new Set(); targetSetByLine.set(via, set) }
    if (set.has(target)) return
    set.add(target)
    const arr = targetsByLine.get(via)
    if (arr) arr.push(target)
    else targetsByLine.set(via, [target])
  }
  for (const edge of edges) {
    const a = edge.fromNodeId
    const b = edge.toNodeId
    if (a === b) continue
    const aLine = keptLineIds.has(a)
    const bLine = keptLineIds.has(b)
    // line↔line: orientation is meaningful (new convention writes via-line → target = fromNodeId),
    // so the from side is the via and the to side is the target. Line↔line is new (#20250), so
    // there is no legacy data to misread here.
    if (aLine && bLine) addTarget(a, b)
    // line ↔ object (symbol / Tee): the line is the via, the object is the target. We accept the
    // edge in EITHER direction so connections from graphs saved BEFORE the via-line → target
    // orientation fix (legacy start anchor was object → line) still export correctly.
    else if (aLine && keptObjectIds.has(b)) addTarget(a, b)
    else if (bLine && keptObjectIds.has(a)) addTarget(b, a)
    // else: not a line-anchored connectivity edge (e.g. has_label symbol → tag) — skip.
  }
  const connections: GoldConnection[] = []
  const seenConnection = new Set<string>()
  for (const [via, targets] of targetsByLine) {
    for (let i = 0; i < targets.length; i++) {
      for (let j = i + 1; j < targets.length; j++) {
        const from = targets[i]
        const to = targets[j]
        // The dedup key is intentionally DIRECTIONAL (from/to not normalized): `from` precedes `to`
        // in the line's endpoint-collection order, and the i<j loop emits each unordered pair once
        // per line, so there are never two orderings of the same (from,to,via). Connectivity itself
        // is undirected — a consumer comparing connections should treat {from,to} as an unordered
        // pair (keyed with `via`); the export does not duplicate or normalize it here.
        const key = `${from} ${to} ${via}`
        if (seenConnection.has(key)) continue
        seenConnection.add(key)
        connections.push({ from, to, via })
      }
    }
  }

  // ── tables (row/column structure preserved) ──────────────────────────────────────────
  // Reuses the same pivot the grid panel reads, so the exported layout is by construction
  // the layout the annotator saw and corrected.
  const tables: GoldTable[] = []
  for (const model of groupCellsIntoTables(nodes)) {
    const cells: GoldTableCell[] = []
    for (let row = 0; row < model.nRows; row++) {
      for (let col = 0; col < model.nCols; col++) {
        const cell = model.cellByKey.get(cellKey(row, col))
        if (!cell) continue // a coordinate the drawing never filled — stays absent, not blank
        if (!isFiniteNumber(cell.x) || !isFiniteNumber(cell.y) || !isFiniteNumber(cell.width) || !isFiniteNumber(cell.height)) continue
        if (cell.width <= 0 || cell.height <= 0) continue
        const bbox = toBBox(cell.x, cell.y, cell.width, cell.height)
        if (!isOrdered(bbox)) continue
        const span = cell.col_span && cell.col_span > 1 ? cell.col_span : undefined
        cells.push({
          id: cell.id,
          row,
          col,
          ...(span ? { col_span: span } : {}),
          // Not dropped when empty: a blank cell is a real observation.
          text: trimOrEmpty(cellText(cell)),
          bbox,
          type: trimOrEmpty(cell.pnid_level_iii),
        })
      }
    }
    if (cells.length === 0) continue // every cell unusable → not ground truth
    // No footprint means no usable geometry for the table as a whole. Emitting [0,0,0,0]
    // would hand the eval a box at the page origin that looks real — drop the table instead.
    const region = model.region
    if (!region) continue
    tables.push({
      table_index: model.tableIndex,
      bbox: toBBox(region.x, region.y, region.width, region.height),
      n_rows: model.nRows,
      n_cols: model.nCols,
      cells,
    })
  }

  // A tag the table already represents would otherwise be scored twice — once loose, once
  // as a cell. Dropping it here (rather than deleting the node) keeps the graph lossless.
  const keptTags = tables.length === 0
    ? tags
    : tags.filter((tag) => {
        const cx = (tag.bbox[0] + tag.bbox[2]) / 2
        const cy = (tag.bbox[1] + tag.bbox[3]) / 2
        return !tables.some((tb) =>
          cx >= tb.bbox[0] && cx <= tb.bbox[2] && cy >= tb.bbox[1] && cy <= tb.bbox[3])
      })

  return {
    drawing_key: meta.drawingKey,
    ...(meta.sheetId ? { sheet_id: meta.sheetId } : {}),
    page: meta.page ?? 1,
    image_size: { width: w, height: h },
    symbols,
    lines,
    tags: keptTags,
    connections,
    tables,
  }
}
